import { useState, useEffect } from 'react';
import { getCurrencySymbol } from '../utils/currency';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FaSortUp, FaSortDown, FaSort, FaEdit, FaTrash, FaSave, FaPlus, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { readGlobalProfiles, fetchCombinations, readFlatTemplate, writeCombination, writeFlatTemplate, deleteTemplateRecord } from '@services/firebaseGlobal';
import { getFirebase } from '@services/firebase';
import { useAlert, useConfirm } from '../contexts/ToastContext';

const Meta = ({ userRole: propUserRole }) => {
  const { auth } = getFirebase();
  const showAlert = useAlert();
  const showConfirm = useConfirm();
  const [user, loading, error] = useAuthState(auth);
  const [userRole, setUserRole] = useState(() => {
    if (propUserRole) return propUserRole;
    try {
      const cachedRole = localStorage.getItem('userRole');
      return cachedRole || '';
    } catch (err) {
      console.error('Error reading userRole from localStorage:', err);
      return '';
    }
  });
  const navigate = useNavigate();
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);

  // State for section expansion
  const [isFlavoursExpanded, setIsFlavoursExpanded] = useState(false);
  const [isCoversExpanded, setIsCoversExpanded] = useState(false);
  const [isEventsExpanded, setIsEventsExpanded] = useState(false);
  const [isShapesExpanded, setIsShapesExpanded] = useState(false);
  const [isHolidayCalendarExpanded, setIsHolidayCalendarExpanded] = useState(false);
  const [isTimewindowExpanded, setIsTimewindowExpanded] = useState(false);
  const [isDeliveryChargesExpanded, setIsDeliveryChargesExpanded] = useState(false);
  const [isVariantChargesExpanded, setIsVariantChargesExpanded] = useState(false);
  const [isOrderSequenceExpanded, setIsOrderSequenceExpanded] = useState(false);
  const [isExceptionSequenceExpanded, setIsExceptionSequenceExpanded] = useState(false);
  const [isProcessControlExpanded, setIsProcessControlExpanded] = useState(false);

  // State for oCombination (Flavours & Fillings)
  const [combinations, setCombinations] = useState([]);
  const [filteredCombinations, setFilteredCombinations] = useState([]);
  const [editingCombinationId, setEditingCombinationId] = useState(null);
  const [editCombinationData, setEditCombinationData] = useState({ flavour: '', filling: '', sponge: '', segment: '', priceSegment: '' });
  const [newCombinationData, setNewCombinationData] = useState({ flavour: '', filling: '', sponge: '', segment: '', priceSegment: '' });
  const [addingCombination, setAddingCombination] = useState(false);

  // State for oCoverCombi (Covers)
  const [coverCombis, setCoverCombis] = useState([]);
  const [filteredCoverCombis, setFilteredCoverCombis] = useState([]);
  const [editingCoverCombiId, setEditingCoverCombiId] = useState(null);
  const [editCoverCombiData, setEditCoverCombiData] = useState({ cover: '', frosting: '', priceSegment: '' });
  const [newCoverCombiData, setNewCoverCombiData] = useState({ cover: '', frosting: '', priceSegment: '' });
  const [addingCoverCombi, setAddingCoverCombi] = useState(false);

  // State for cEvents (Events)
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editEventData, setEditEventData] = useState({ id: '', name: '' });
  const [newEventData, setNewEventData] = useState({ id: '', name: '' });
  const [addingEvent, setAddingEvent] = useState(false);

  // State for cShape (Shapes)
  const [shapes, setShapes] = useState([]);
  const [filteredShapes, setFilteredShapes] = useState([]);
  const [editingShapeId, setEditingShapeId] = useState(null);
  const [editShapeData, setEditShapeData] = useState({ id: '', name: '' });
  const [newShapeData, setNewShapeData] = useState({ id: '', name: '' });
  const [addingShape, setAddingShape] = useState(false);

  // State for otimeline (Order Sequence)
  const [orderSequences, setOrderSequences] = useState([]);
  const [filteredOrderSequences, setFilteredOrderSequences] = useState([]);
  const [editingOrderSequenceId, setEditingOrderSequenceId] = useState(null);
  const [editOrderSequenceData, setEditOrderSequenceData] = useState({ id: '', name: '' });
  const [newOrderSequenceData, setNewOrderSequenceData] = useState({ id: '', name: '' });
  const [addingOrderSequence, setAddingOrderSequence] = useState(false);

  // State for oexptimeline (Order Exception Sequence)
  const [exceptionSequences, setExceptionSequences] = useState([]);
  const [filteredExceptionSequences, setFilteredExceptionSequences] = useState([]);
  const [editingExceptionSequenceId, setEditingExceptionSequenceId] = useState(null);
  const [editExceptionSequenceData, setEditExceptionSequenceData] = useState({ id: '', requestState: '', showState1: '', showState2: '' });
  const [newExceptionSequenceData, setNewExceptionSequenceData] = useState({ id: '', requestState: '', showState1: '', showState2: '' });
  const [addingExceptionSequence, setAddingExceptionSequence] = useState(false);

  // State for oprscontrol (Order Process Control)
  const [processControls, setProcessControls] = useState([]);
  const [filteredProcessControls, setFilteredProcessControls] = useState([]);
  const [editingProcessControlId, setEditingProcessControlId] = useState(null);
  const [editProcessControlData, setEditProcessControlData] = useState({ id: '', application: '', activity: '', state: '' });
  const [newProcessControlData, setNewProcessControlData] = useState({ id: '', application: '', activity: '', state: '' });
  const [addingProcessControl, setAddingProcessControl] = useState(false);

  // State for dHolidayCalendar (Holiday Calendar)
  const [holidayCalendars, setHolidayCalendars] = useState([]);
  const [filteredHolidayCalendars, setFilteredHolidayCalendars] = useState([]);
  const [editingHolidayCalendarId, setEditingHolidayCalendarId] = useState(null);
  const [editHolidayCalendarData, setEditHolidayCalendarData] = useState({ id: '', day: '' });
  const [newHolidayCalendarData, setNewHolidayCalendarData] = useState({ id: '', day: '' });
  const [addingHolidayCalendar, setAddingHolidayCalendar] = useState(false);

  // State for dTimewindow (Delivery Timewindow)
  const [timewindows, setTimewindows] = useState([]);
  const [filteredTimewindows, setFilteredTimewindows] = useState([]);
  const [editingTimewindowId, setEditingTimewindowId] = useState(null);
  const [editTimewindowData, setEditTimewindowData] = useState({ id: '', window: '' });
  const [newTimewindowData, setNewTimewindowData] = useState({ id: '', window: '' });
  const [addingTimewindow, setAddingTimewindow] = useState(false);

  // State for cCakeCharge (Variant Charges)
  const [variantCharges, setVariantCharges] = useState([]);
  const [filteredVariantCharges, setFilteredVariantCharges] = useState([]);
  const [editingVariantChargeId, setEditingVariantChargeId] = useState(null);
  const [editVariantChargeData, setEditVariantChargeData] = useState({ variant: '', cost: '', personServed: '' });
  const [newVariantChargeData, setNewVariantChargeData] = useState({ variant: '', cost: '', personServed: '' });
  const [addingVariantCharge, setAddingVariantCharge] = useState(false);

  // State for dDeliveryCharge (Delivery Charges)
  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [filteredDeliveryCharges, setFilteredDeliveryCharges] = useState([]);
  const [editingDeliveryChargeId, setEditingDeliveryChargeId] = useState(null);
  const [editDeliveryChargeData, setEditDeliveryChargeData] = useState({ distance: '', cost: '' });
  const [newDeliveryChargeData, setNewDeliveryChargeData] = useState({ distance: '', cost: '' });
  const [addingDeliveryCharge, setAddingDeliveryCharge] = useState(false);

  // Search and Sort States
  const [searchQuery, setSearchQuery] = useState('');
  const [combinationSortConfig, setCombinationSortConfig] = useState({ key: 'flavour', direction: 'asc' });
  const [coverCombiSortConfig, setCoverCombiSortConfig] = useState({ key: 'cover', direction: 'asc' });
  const [eventSortConfig, setEventSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [shapeSortConfig, setShapeSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [orderSequenceSortConfig, setOrderSequenceSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [exceptionSequenceSortConfig, setExceptionSequenceSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [processControlSortConfig, setProcessControlSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [holidayCalendarSortConfig, setHolidayCalendarSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [timewindowSortConfig, setTimewindowSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [deliveryChargeSortConfig, setDeliveryChargeSortConfig] = useState({ key: 'distance', direction: 'asc' });
  const [variantChargeSortConfig, setVariantChargeSortConfig] = useState({ key: 'variant', direction: 'asc' });

  // Helper function to extract the numeric part of the ID (e.g., "id10" -> 10)
  const extractIdNumber = (id) => {
    const match = id.match(/^id(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Updated to include 'mover' prefix
  const extractUserSuperPrefixAndNumber = (id) => {
    const match = id.match(/^(user|buyer|super|admin|mover)(\d+)$/);
    return match ? { prefix: match[1], number: parseInt(match[2], 10) } : { prefix: id, number: 0 };
  };

  const extractExceptionSequencePrefixAndNumber = (id) => {
    const match = id.match(/^(change|option|stop)(\d+)$/);
    return match ? { prefix: match[1], number: parseInt(match[2], 10) } : { prefix: id, number: 0 };
  };

  const extractHolidayCalendarPrefixAndNumber = (id) => {
    const match = id.match(/^(holiday|offday)(\d+)$/);
    return match ? { prefix: match[1], number: parseInt(match[2], 10) } : { prefix: id, number: 0 };
  };

  const extractTimewindowPrefixAndNumber = (id) => {
    const match = id.match(/^(weekdays|weekend)(\d+)$/);
    return match ? { prefix: match[1], number: parseInt(match[2], 10) } : { prefix: id, number: 0 };
  };

  // Fetch user role and restrict access
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const userList = await readGlobalProfiles('cUsers');
          const currentUserData = userList.find(userData => userData.id === user.uid);
          if (currentUserData) {
            let role = 'User';
            if (currentUserData.isadminuser) {
              role = 'Admin';
            } else if (currentUserData.issuperuser) {
              role = 'Super';
            } else if (currentUserData.isbuyer) {
              role = 'Buyer';
            } else if (currentUserData.ismover) {
              role = 'Mover';
            }
            setUserRole(role);
            try {
              localStorage.setItem('userRole', role);
            } catch (err) {
              console.error('Error saving userRole to localStorage:', err);
            }

            // Restrict access to only Admin and Super users
            if (role !== 'Admin' && role !== 'Super') {
              showAlert('Access denied: You must be an admin or super user to view this page.');
              navigate('/profile');
            }
          } else {
            showAlert('Access denied: User data not found.');
            navigate('/profile');
          }
        } catch (err) {
          console.error('Error fetching user role:', err);
          showAlert('Error fetching user role. Please try again.');
          navigate('/profile');
        }
      }
    };

    if (!propUserRole) {
      fetchUserRole();
    } else if (propUserRole !== 'Admin' && propUserRole !== 'Super') {
      showAlert('Access denied: You must be an admin or super user to view this page.');
      navigate('/profile');
    }
  }, [user, navigate, propUserRole]);

  // Update isOperationInProgress whenever any editing or adding state changes
  useEffect(() => {
    const operationInProgress = (
      editingCombinationId !== null ||
      addingCombination ||
      editingCoverCombiId !== null ||
      addingCoverCombi ||
      editingEventId !== null ||
      addingEvent ||
      editingShapeId !== null ||
      addingShape ||
      editingOrderSequenceId !== null ||
      addingOrderSequence ||
      editingExceptionSequenceId !== null ||
      addingExceptionSequence ||
      editingProcessControlId !== null ||
      addingProcessControl ||
      editingHolidayCalendarId !== null ||
      addingHolidayCalendar ||
      editingTimewindowId !== null ||
      addingTimewindow ||
      editingDeliveryChargeId !== null ||
      addingDeliveryCharge ||
      editingVariantChargeId !== null ||
      addingVariantCharge
    );
    setIsOperationInProgress(operationInProgress);
  }, [
    editingCombinationId, addingCombination,
    editingCoverCombiId, addingCoverCombi,
    editingEventId, addingEvent,
    editingShapeId, addingShape,
    editingOrderSequenceId, addingOrderSequence,
    editingExceptionSequenceId, addingExceptionSequence,
    editingProcessControlId, addingProcessControl,
    editingHolidayCalendarId, addingHolidayCalendar,
    editingTimewindowId, addingTimewindow,
    editingDeliveryChargeId, addingDeliveryCharge,
    editingVariantChargeId, addingVariantCharge
  ]);

  // Fetch data from otemplate tables
  const fetchCombinationsData = async () => {
    try {
      const oCombinations = await fetchCombinations('oCombination');
      setCombinations(oCombinations);
      applyCombinationFiltersAndSort(oCombinations);

      const oCoverCombis = await fetchCombinations('oCoverCombi');
      setCoverCombis(oCoverCombis);
      applyCoverCombiFiltersAndSort(oCoverCombis);

      const eventsData = await readFlatTemplate('cEvents');
      setEvents(eventsData);
      applyEventFiltersAndSort(eventsData);

      const shapesData = await readFlatTemplate('cShape');
      setShapes(shapesData);
      applyShapeFiltersAndSort(shapesData);

      const orderSequencesData = await readFlatTemplate('otimeline');
      setOrderSequences(orderSequencesData);
      applyOrderSequenceFiltersAndSort(orderSequencesData);

      const exceptionSequencesData = await fetchCombinations('oexptimeline');
      setExceptionSequences(exceptionSequencesData);
      applyExceptionSequenceFiltersAndSort(exceptionSequencesData);

      const processControlsData = await fetchCombinations('oprscontrol');
      setProcessControls(processControlsData);
      applyProcessControlFiltersAndSort(processControlsData);

      const holidayCalendarsData = await readFlatTemplate('dHolidayCalendar');
      setHolidayCalendars(holidayCalendarsData);
      applyHolidayCalendarFiltersAndSort(holidayCalendarsData);

      const timewindowsData = await readFlatTemplate('dTimewindow');
      setTimewindows(timewindowsData);
      applyTimewindowFiltersAndSort(timewindowsData);

      const deliveryChargesData = await fetchCombinations('dDeliveryCharge');
      setDeliveryCharges(deliveryChargesData);
      applyDeliveryChargeFiltersAndSort(deliveryChargesData);

      const variantChargesData = await fetchCombinations('cCakeCharge');
      setVariantCharges(variantChargesData);
      applyVariantChargeFiltersAndSort(variantChargesData);
    } catch (error) {
      console.error('Error fetching otemplate data:', error);
    }
  };

  useEffect(() => {
    if (userRole !== 'Admin' && userRole !== 'Super') {
      return;
    }
  
    fetchCombinationsData();
  
    let intervalId;
    if (!isOperationInProgress) {
      intervalId = setInterval(fetchCombinationsData, 15000);
    }
  
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [userRole, isOperationInProgress]);

  // Filter and Sort Functions
  const applyCombinationFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.flavour.toLowerCase().includes(query) ||
        item.filling.toLowerCase().includes(query) ||
        (Array.isArray(item.sponge) ? item.sponge.some(s => s.toLowerCase().includes(query)) : item.sponge?.toLowerCase().includes(query)) ||
        (Array.isArray(item.segment) ? item.segment.some(s => s.toLowerCase().includes(query)) : item.segment?.toLowerCase().includes(query))
      );
    }

    if (combinationSortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[combinationSortConfig.key];
        let bValue = b[combinationSortConfig.key];
        if (combinationSortConfig.key === 'sponge') {
          aValue = a.sponge[0];
          bValue = b.sponge[0];
        }
        if (aValue < bValue) return combinationSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return combinationSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCombinations(filtered);
  };

  const applyCoverCombiFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.cover.toLowerCase().includes(query) ||
        item.frosting.toLowerCase().includes(query)
      );
    }

    if (coverCombiSortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[coverCombiSortConfig.key];
        let bValue = b[coverCombiSortConfig.key];
        if (aValue < bValue) return coverCombiSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return coverCombiSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredCoverCombis(filtered);
  };

  const applyVariantChargeFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.variant.toLowerCase().includes(query) ||
        item.cost.toLowerCase().includes(query) ||
        (item.personServed || '').toLowerCase().includes(query)
      );
    }
    if (variantChargeSortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[variantChargeSortConfig.key];
        const bValue = b[variantChargeSortConfig.key];
        if (aValue < bValue) return variantChargeSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return variantChargeSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    setFilteredVariantCharges(filtered);
  };

  const applyEventFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.id.toLowerCase().includes(query) || 
        item.name.toLowerCase().includes(query)
      );
    }

    if (eventSortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = eventSortConfig.key === 'id' ? extractIdNumber(a[eventSortConfig.key]) : a[eventSortConfig.key];
        let bValue = eventSortConfig.key === 'id' ? extractIdNumber(b[eventSortConfig.key]) : b[eventSortConfig.key];
        if (aValue < bValue) return eventSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return eventSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredEvents(filtered);
  };

  const applyShapeFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.id.toLowerCase().includes(query) || 
        item.name.toLowerCase().includes(query)
      );
    }

    if (shapeSortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = shapeSortConfig.key === 'id' ? extractIdNumber(a[shapeSortConfig.key]) : a[shapeSortConfig.key];
        let bValue = shapeSortConfig.key === 'id' ? extractIdNumber(b[shapeSortConfig.key]) : b[shapeSortConfig.key];
        if (aValue < bValue) return shapeSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return shapeSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredShapes(filtered);
  };

  const applyHolidayCalendarFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.id.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query)
      );
    }

    if (holidayCalendarSortConfig.key) {
      filtered.sort((a, b) => {
        if (holidayCalendarSortConfig.key === 'id') {
          const aParts = extractHolidayCalendarPrefixAndNumber(a.id);
          const bParts = extractHolidayCalendarPrefixAndNumber(b.id);

          if (aParts.prefix !== bParts.prefix) {
            return aParts.prefix === 'holiday' ? -1 : 1;
          }

          const aNumber = aParts.number;
          const bNumber = bParts.number;
          if (aNumber < bNumber) return holidayCalendarSortConfig.direction === 'asc' ? -1 : 1;
          if (aNumber > bNumber) return holidayCalendarSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        } else {
          let aValue = a[holidayCalendarSortConfig.key];
          let bValue = b[holidayCalendarSortConfig.key];
          if (aValue < bValue) return holidayCalendarSortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return holidayCalendarSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }

    setFilteredHolidayCalendars(filtered);
  };

  const applyTimewindowFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.id.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query)
      );
    }

    if (timewindowSortConfig.key) {
      filtered.sort((a, b) => {
        if (timewindowSortConfig.key === 'id') {
          const aParts = extractTimewindowPrefixAndNumber(a.id);
          const bParts = extractTimewindowPrefixAndNumber(b.id);

          if (aParts.prefix !== bParts.prefix) {
            return aParts.prefix === 'weekdays' ? -1 : 1;
          }

          const aNumber = aParts.number;
          const bNumber = bParts.number;
          if (aNumber < bNumber) return timewindowSortConfig.direction === 'asc' ? -1 : 1;
          if (aNumber > bNumber) return timewindowSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        } else {
          let aValue = a[timewindowSortConfig.key];
          let bValue = b[timewindowSortConfig.key];
          if (aValue < bValue) return timewindowSortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return timewindowSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }

    setFilteredTimewindows(filtered);
  };

  const applyDeliveryChargeFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.distance.toLowerCase().includes(query) ||
        item.cost.toLowerCase().includes(query)
      );
    }

    if (deliveryChargeSortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = parseFloat(a[deliveryChargeSortConfig.key]) || 0;
        const bVal = parseFloat(b[deliveryChargeSortConfig.key]) || 0;
        if (aVal < bVal) return deliveryChargeSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return deliveryChargeSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredDeliveryCharges(filtered);
  };

  const applyOrderSequenceFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => 
        item.id.toLowerCase().includes(query) || 
        item.name.toLowerCase().includes(query)
      );
    }

    if (orderSequenceSortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = orderSequenceSortConfig.key === 'id' ? extractIdNumber(a[orderSequenceSortConfig.key]) : a[orderSequenceSortConfig.key];
        let bValue = orderSequenceSortConfig.key === 'id' ? extractIdNumber(b[orderSequenceSortConfig.key]) : b[orderSequenceSortConfig.key];
        if (aValue < bValue) return orderSequenceSortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return orderSequenceSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredOrderSequences(filtered);
  };

  const applyExceptionSequenceFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.id.toLowerCase().includes(query) ||
        item.requestState.toLowerCase().includes(query) ||
        item.showState1.toLowerCase().includes(query) ||
        item.showState2.toLowerCase().includes(query)
      );
    }
  
    if (exceptionSequenceSortConfig.key) {
      filtered.sort((a, b) => {
        if (exceptionSequenceSortConfig.key === 'id') {
          const aParts = extractExceptionSequencePrefixAndNumber(a.id);
          const bParts = extractExceptionSequencePrefixAndNumber(b.id);
  
          // Sort by prefix first: change < option < stop
          if (aParts.prefix !== bParts.prefix) {
            const prefixOrder = { change: 1, option: 2, stop: 3 };
            return (prefixOrder[aParts.prefix] - prefixOrder[bParts.prefix]) * (exceptionSequenceSortConfig.direction === 'asc' ? 1 : -1);
          }
  
          // Within the same prefix, sort by number
          const aNumber = aParts.number;
          const bNumber = bParts.number;
          if (aNumber < bNumber) return exceptionSequenceSortConfig.direction === 'asc' ? -1 : 1;
          if (aNumber > bNumber) return exceptionSequenceSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        } else {
          // Sort by other fields (requestState, showState1, showState2)
          let aValue = a[exceptionSequenceSortConfig.key];
          let bValue = b[exceptionSequenceSortConfig.key];
          if (aValue < bValue) return exceptionSequenceSortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return exceptionSequenceSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }
  
    setFilteredExceptionSequences(filtered);
  };

  const applyProcessControlFiltersAndSort = (dataToFilter) => {
    let filtered = [...dataToFilter];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.id.toLowerCase().includes(query) ||
        item.application.toLowerCase().includes(query) ||
        item.activity.toLowerCase().includes(query) ||
        item.state.toLowerCase().includes(query)
      );
    }
  
    if (processControlSortConfig.key) {
      filtered.sort((a, b) => {
        if (processControlSortConfig.key === 'id') {
          const aParts = extractUserSuperPrefixAndNumber(a.id);
          const bParts = extractUserSuperPrefixAndNumber(b.id);
  
          // Sort by prefix: buyer < super < admin < mover
          if (aParts.prefix !== bParts.prefix) {
            const prefixOrder = { user: 1, buyer: 1, super: 2, admin: 3, mover: 4 };
            return (prefixOrder[aParts.prefix] - prefixOrder[bParts.prefix]) * (processControlSortConfig.direction === 'asc' ? 1 : -1);
          }
  
          // Within the same prefix, sort by number
          const aNumber = aParts.number;
          const bNumber = bParts.number;
          if (aNumber < bNumber) return processControlSortConfig.direction === 'asc' ? -1 : 1;
          if (aNumber > bNumber) return processControlSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        } else {
          // Sort by other fields (application, activity, state)
          let aValue = a[processControlSortConfig.key];
          let bValue = b[processControlSortConfig.key];
          if (aValue < bValue) return processControlSortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return processControlSortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    }
  
    setFilteredProcessControls(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClose = () => navigate(-1);

  const handleSort = (key, setSortConfig, applyFiltersAndSort, data) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
    applyFiltersAndSort(data);
  };

  useEffect(() => {
    applyCombinationFiltersAndSort(combinations);
  }, [combinations, searchQuery, combinationSortConfig]);

  useEffect(() => {
    applyCoverCombiFiltersAndSort(coverCombis);
  }, [coverCombis, searchQuery, coverCombiSortConfig]);

  useEffect(() => {
    applyEventFiltersAndSort(events);
  }, [events, searchQuery, eventSortConfig]);

  useEffect(() => {
    applyShapeFiltersAndSort(shapes);
  }, [shapes, searchQuery, shapeSortConfig]);

  useEffect(() => {
    applyHolidayCalendarFiltersAndSort(holidayCalendars);
  }, [holidayCalendars, searchQuery, holidayCalendarSortConfig]);

  useEffect(() => {
    applyTimewindowFiltersAndSort(timewindows);
  }, [timewindows, searchQuery, timewindowSortConfig]);

  useEffect(() => {
    applyDeliveryChargeFiltersAndSort(deliveryCharges);
  }, [deliveryCharges, searchQuery, deliveryChargeSortConfig]);

  useEffect(() => {
    applyVariantChargeFiltersAndSort(variantCharges);
  }, [variantCharges, searchQuery, variantChargeSortConfig]);

  useEffect(() => {
    applyOrderSequenceFiltersAndSort(orderSequences);
  }, [orderSequences, searchQuery, orderSequenceSortConfig]);

  useEffect(() => {
    applyExceptionSequenceFiltersAndSort(exceptionSequences);
  }, [exceptionSequences, searchQuery, exceptionSequenceSortConfig]);

  useEffect(() => {
    applyProcessControlFiltersAndSort(processControls);
  }, [processControls, searchQuery, processControlSortConfig]);

  // CRUD Handlers for oCombination
  const handleEditCombination = (item) => {
    setEditingCombinationId(item.id);
    setEditCombinationData({ flavour: item.flavour, filling: item.filling, sponge: item.sponge[0], segment: item.segment, priceSegment: item.priceSegment || '' });
  };

  const handleSaveCombination = async (id) => {
    try {
      await writeCombination('oCombination', [editCombinationData.flavour, editCombinationData.filling, editCombinationData.sponge, editCombinationData.segment, editCombinationData.priceSegment], id);
      setEditingCombinationId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving combination:', error);
      showAlert(`Failed to save combination: ${error.message}`);
    }
  };

  const handleAddCombination = async () => {
    try {
      await writeCombination('oCombination', [newCombinationData.flavour, newCombinationData.filling, newCombinationData.sponge, newCombinationData.segment, newCombinationData.priceSegment]);
      setAddingCombination(false);
      setNewCombinationData({ flavour: '', filling: '', sponge: '', segment: '', priceSegment: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding combination:', error);
      showAlert(`Failed to add combination: ${error.message}`);
    }
  };

  const handleDeleteCombination = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this combination?'))) return;
    try {
      await deleteTemplateRecord('oCombination', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting combination:', error);
      showAlert(`Failed to delete combination: ${error.message}`);
    }
  };

  // CRUD Handlers for oCoverCombi
  const handleEditCoverCombi = (item) => {
    setEditingCoverCombiId(item.id);
    setEditCoverCombiData({ cover: item.cover, frosting: item.frosting, priceSegment: item.priceSegment || '' });
  };

  const handleSaveCoverCombi = async (id) => {
    try {
      await writeCombination('oCoverCombi', [editCoverCombiData.cover, editCoverCombiData.frosting, editCoverCombiData.priceSegment], id);
      setEditingCoverCombiId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving cover combination:', error);
      showAlert(`Failed to save cover combination: ${error.message}`);
    }
  };

  const handleAddCoverCombi = async () => {
    try {
      await writeCombination('oCoverCombi', [newCoverCombiData.cover, newCoverCombiData.frosting, newCoverCombiData.priceSegment]);
      setAddingCoverCombi(false);
      setNewCoverCombiData({ cover: '', frosting: '', priceSegment: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding cover combination:', error);
      showAlert(`Failed to add cover combination: ${error.message}`);
    }
  };

  const handleDeleteCoverCombi = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this cover combination?'))) return;
    try {
      await deleteTemplateRecord('oCoverCombi', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting cover combination:', error);
      showAlert(`Failed to delete cover combination: ${error.message}`);
    }
  };

  // CRUD Handlers for cCakeCharge (Variant Charges)
  const handleEditVariantCharge = (item) => {
    setEditingVariantChargeId(item.id);
    setEditVariantChargeData({ variant: item.variant, cost: item.cost, personServed: item.personServed || '' });
  };

  const handleSaveVariantCharge = async (id) => {
    try {
      await writeCombination('cCakeCharge', [editVariantChargeData.variant, editVariantChargeData.cost, editVariantChargeData.personServed], id);
      setEditingVariantChargeId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving variant charge:', error);
      showAlert(`Failed to save variant charge: ${error.message}`);
    }
  };

  const handleAddVariantCharge = async () => {
    try {
      await writeCombination('cCakeCharge', [newVariantChargeData.variant, newVariantChargeData.cost, newVariantChargeData.personServed]);
      setAddingVariantCharge(false);
      setNewVariantChargeData({ variant: '', cost: '', personServed: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding variant charge:', error);
      showAlert(`Failed to add variant charge: ${error.message}`);
    }
  };

  const handleDeleteVariantCharge = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this variant charge?'))) return;
    try {
      await deleteTemplateRecord('cCakeCharge', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting variant charge:', error);
      showAlert(`Failed to delete variant charge: ${error.message}`);
    }
  };

  // CRUD Handlers for cEvents
  const handleEditEvent = (item) => {
    setEditingEventId(item.id);
    setEditEventData({ id: item.id, name: item.name });
  };

  const handleSaveEvent = async (id) => {
    try {
      if (editEventData.id !== id) {
        const existingEvent = events.find(event => event.id === editEventData.id);
        if (existingEvent) {
          throw new Error(`ID "${editEventData.id}" already exists in cEvents. Please choose a different ID.`);
        }
        await writeFlatTemplate('cEvents', editEventData.name, editEventData.id);
        await deleteTemplateRecord('cEvents', id);
      } else {
        await writeFlatTemplate('cEvents', editEventData.name, id);
      }
      setEditingEventId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving event:', error);
      showAlert(`Failed to save event: ${error.message}`);
    }
  };

  const handleAddEvent = async () => {
    try {
      if (!newEventData.name) {
        throw new Error('Event name is required.');
      }
      const eventId = newEventData.id ? newEventData.id.trim() : undefined;
      if (eventId) {
        if (eventId.includes('/')) {
          throw new Error('ID cannot contain slashes (/). Please choose a different ID.');
        }
        const existingEvent = events.find(event => event.id === eventId);
        if (existingEvent) {
          throw new Error(`ID "${eventId}" already exists in cEvents. Please choose a different ID.`);
        }
      }
      await writeFlatTemplate('cEvents', newEventData.name, eventId);
      setAddingEvent(false);
      setNewEventData({ id: '', name: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding event:', error.message, error.stack);
      showAlert(`Failed to add event: ${error.message}`);
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this event?'))) return;
    try {
      await deleteTemplateRecord('cEvents', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting event:', error);
      showAlert(`Failed to delete event: ${error.message}`);
    }
  };

  // CRUD Handlers for cShape
  const handleEditShape = (item) => {
    setEditingShapeId(item.id);
    setEditShapeData({ id: item.id, name: item.name });
  };

  const handleSaveShape = async (id) => {
    try {
      if (editShapeData.id !== id) {
        const existingShape = shapes.find(shape => shape.id === editShapeData.id);
        if (existingShape) {
          throw new Error(`ID "${editShapeData.id}" already exists in cShape. Please choose a different ID.`);
        }
        await writeFlatTemplate('cShape', editShapeData.name, editShapeData.id);
        await deleteTemplateRecord('cShape', id);
      } else {
        await writeFlatTemplate('cShape', editShapeData.name, id);
      }
      setEditingShapeId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving shape:', error);
      showAlert(`Failed to save shape: ${error.message}`);
    }
  };

  const handleAddShape = async () => {
    try {
      if (!newShapeData.name) {
        throw new Error('Shape name is required.');
      }
      const shapeId = newShapeData.id ? newShapeData.id.trim() : undefined;
      if (shapeId) {
        if (shapeId.includes('/')) {
          throw new Error('ID cannot contain slashes (/). Please choose a different ID.');
        }
        const existingShape = shapes.find(shape => shape.id === shapeId);
        if (existingShape) {
          throw new Error(`ID "${shapeId}" already exists in cShape. Please choose a different ID.`);
        }
      }
      await writeFlatTemplate('cShape', newShapeData.name, shapeId);
      setAddingShape(false);
      setNewShapeData({ id: '', name: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding shape:', error.message, error.stack);
      showAlert(`Failed to add shape: ${error.message}`);
    }
  };

  const handleDeleteShape = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this shape?'))) return;
    try {
      await deleteTemplateRecord('cShape', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting shape:', error);
      showAlert(`Failed to delete shape: ${error.message}`);
    }
  };

  // CRUD Handlers for dHolidayCalendar (Holiday Calendar)
  const handleEditHolidayCalendar = (item) => {
    setEditingHolidayCalendarId(item.id);
    setEditHolidayCalendarData({ id: item.id, day: item.name });
  };

  const handleSaveHolidayCalendar = async (id) => {
    try {
      if (editHolidayCalendarData.id !== id) {
        const existingHolidayCalendar = holidayCalendars.find(holiday => holiday.id === editHolidayCalendarData.id);
        if (existingHolidayCalendar) {
          throw new Error(`ID "${editHolidayCalendarData.id}" already exists in dHolidayCalendar. Please choose a different ID.`);
        }
        await writeFlatTemplate('dHolidayCalendar', editHolidayCalendarData.day, editHolidayCalendarData.id);
        await deleteTemplateRecord('dHolidayCalendar', id);
      } else {
        await writeFlatTemplate('dHolidayCalendar', editHolidayCalendarData.day, id);
      }
      setEditingHolidayCalendarId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving holiday calendar:', error);
      showAlert(`Failed to save holiday calendar: ${error.message}`);
    }
  };

  const handleAddHolidayCalendar = async () => {
    try {
      if (!newHolidayCalendarData.id) {
        throw new Error('ID is required in the format holiday<number> or offday<number>.');
      }
      if (!newHolidayCalendarData.day) {
        throw new Error('Day is required.');
      }
      const holidayId = newHolidayCalendarData.id.trim();
      if (holidayId.includes('/')) {
        throw new Error('ID cannot contain slashes (/). Please choose a different ID.');
      }
      if (!holidayId.match(/^(holiday|offday)\d+$/)) {
        throw new Error('ID must be in the format "holiday<number>" or "offday<number>" (e.g., holiday1, offday2).');
      }
      const existingHolidayCalendar = holidayCalendars.find(holiday => holiday.id === holidayId);
      if (existingHolidayCalendar) {
        throw new Error(`ID "${holidayId}" already exists in dHolidayCalendar. Please choose a different ID.`);
      }
      await writeFlatTemplate('dHolidayCalendar', newHolidayCalendarData.day, holidayId);
      setAddingHolidayCalendar(false);
      setNewHolidayCalendarData({ id: '', day: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding holiday calendar:', error.message, error.stack);
      showAlert(`Failed to add holiday calendar: ${error.message}`);
    }
  };

  const handleDeleteHolidayCalendar = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this holiday calendar entry?'))) return;
    try {
      await deleteTemplateRecord('dHolidayCalendar', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting holiday calendar:', error);
      showAlert(`Failed to delete holiday calendar: ${error.message}`);
    }
  };

  // CRUD Handlers for dTimewindow (Delivery Timewindow)
  const validateTimewindowFormat = (value) => {
    const regex = /^\d{2} Hour - \d{2} Hour$/;
    if (!regex.test(value)) {
      throw new Error('Window must be in the format "XX Hour - YY Hour" (e.g., "09 Hour - 12 Hour").');
    }
    const [start, end] = value.split(' Hour - ').map(part => parseInt(part));
    if (start < 0 || start > 23 || end < 0 || end > 23) {
      throw new Error('Hours must be between 00 and 23.');
    }
    if (start >= end) {
      throw new Error('Start hour must be less than end hour.');
    }
  };

  const handleEditTimewindow = (item) => {
    setEditingTimewindowId(item.id);
    setEditTimewindowData({ id: item.id, window: item.name });
  };

  const handleSaveTimewindow = async (id) => {
    try {
      validateTimewindowFormat(editTimewindowData.window);
      if (editTimewindowData.id !== id) {
        const existingTimewindow = timewindows.find(timewindow => timewindow.id === editTimewindowData.id);
        if (existingTimewindow) {
          throw new Error(`ID "${editTimewindowData.id}" already exists in dTimewindow. Please choose a different ID.`);
        }
        await writeFlatTemplate('dTimewindow', editTimewindowData.window, editTimewindowData.id);
        await deleteTemplateRecord('dTimewindow', id);
      } else {
        await writeFlatTemplate('dTimewindow', editTimewindowData.window, id);
      }
      setEditingTimewindowId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving timewindow:', error);
      showAlert(`Failed to save timewindow: ${error.message}`);
    }
  };

  const handleAddTimewindow = async () => {
    try {
      if (!newTimewindowData.id) {
        throw new Error('ID is required in the format weekdays<number> or weekend<number>.');
      }
      if (!newTimewindowData.window) {
        throw new Error('Window is required.');
      }
      const timewindowId = newTimewindowData.id.trim();
      if (timewindowId.includes('/')) {
        throw new Error('ID cannot contain slashes (/). Please choose a different ID.');
      }
      if (!timewindowId.match(/^(weekdays|weekend)\d+$/)) {
        throw new Error('ID must be in the format "weekdays<number>" or "weekend<number>" (e.g., weekdays1, weekend2).');
      }
      const existingTimewindow = timewindows.find(timewindow => timewindow.id === timewindowId);
      if (existingTimewindow) {
        throw new Error(`ID "${timewindowId}" already exists in dTimewindow. Please choose a different ID.`);
      }
      validateTimewindowFormat(newTimewindowData.window);
      await writeFlatTemplate('dTimewindow', newTimewindowData.window, timewindowId);
      setAddingTimewindow(false);
      setNewTimewindowData({ id: '', window: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding timewindow:', error.message, error.stack);
      showAlert(`Failed to add timewindow: ${error.message}`);
    }
  };

  const handleDeleteTimewindow = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this timewindow?'))) return;
    try {
      await deleteTemplateRecord('dTimewindow', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting timewindow:', error);
      showAlert(`Failed to delete timewindow: ${error.message}`);
    }
  };

  // CRUD Handlers for dDeliveryCharge (Delivery Charges)
  const handleEditDeliveryCharge = (item) => {
    setEditingDeliveryChargeId(item.id);
    setEditDeliveryChargeData({ distance: item.distance, cost: item.cost });
  };

  const handleSaveDeliveryCharge = async (id) => {
    try {
      if (!editDeliveryChargeData.distance) throw new Error('Distance is required.');
      if (!editDeliveryChargeData.cost) throw new Error('Cost is required.');
      await writeCombination('dDeliveryCharge', [editDeliveryChargeData.distance, editDeliveryChargeData.cost], id);
      setEditingDeliveryChargeId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving delivery charge:', error);
      showAlert(`Failed to save delivery charge: ${error.message}`);
    }
  };

  const handleAddDeliveryCharge = async () => {
    try {
      if (!newDeliveryChargeData.distance) throw new Error('Distance is required.');
      if (!newDeliveryChargeData.cost) throw new Error('Cost is required.');
      await writeCombination('dDeliveryCharge', [newDeliveryChargeData.distance, newDeliveryChargeData.cost]);
      setAddingDeliveryCharge(false);
      setNewDeliveryChargeData({ distance: '', cost: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding delivery charge:', error);
      showAlert(`Failed to add delivery charge: ${error.message}`);
    }
  };

  const handleDeleteDeliveryCharge = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this delivery charge?'))) return;
    try {
      await deleteTemplateRecord('dDeliveryCharge', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting delivery charge:', error);
      showAlert(`Failed to delete delivery charge: ${error.message}`);
    }
  };

  // CRUD Handlers for otimeline (Order Sequence)
  const handleEditOrderSequence = (item) => {
    setEditingOrderSequenceId(item.id);
    setEditOrderSequenceData({ id: item.id, name: item.name });
  };

  const handleSaveOrderSequence = async (id) => {
    try {
      if (editOrderSequenceData.id !== id) {
        const existingOrderSequence = orderSequences.find(sequence => sequence.id === editOrderSequenceData.id);
        if (existingOrderSequence) {
          throw new Error(`ID "${editOrderSequenceData.id}" already exists in otimeline. Please choose a different ID.`);
        }
        await writeFlatTemplate('otimeline', editOrderSequenceData.name, editOrderSequenceData.id);
        await deleteTemplateRecord('otimeline', id);
      } else {
        await writeFlatTemplate('otimeline', editOrderSequenceData.name, id);
      }
      setEditingOrderSequenceId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving order sequence:', error);
      showAlert(`Failed to save order sequence: ${error.message}`);
    }
  };

  const handleAddOrderSequence = async () => {
    try {
      if (!newOrderSequenceData.name) {
        throw new Error('Order sequence name is required.');
      }
      const sequenceId = newOrderSequenceData.id ? newOrderSequenceData.id.trim() : undefined;
      if (sequenceId) {
        if (sequenceId.includes('/')) {
          throw new Error('ID cannot contain slashes (/). Please choose a different ID.');
        }
        const existingOrderSequence = orderSequences.find(sequence => sequence.id === sequenceId);
        if (existingOrderSequence) {
          throw new Error(`ID "${sequenceId}" already exists in otimeline. Please choose a different ID.`);
        }
      }
      await writeFlatTemplate('otimeline', newOrderSequenceData.name, sequenceId);
      setAddingOrderSequence(false);
      setNewOrderSequenceData({ id: '', name: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding order sequence:', error.message, error.stack);
      showAlert(`Failed to add order sequence: ${error.message}`);
    }
  };

  const handleDeleteOrderSequence = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this order sequence?'))) return;
    try {
      await deleteTemplateRecord('otimeline', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting order sequence:', error);
      showAlert(`Failed to delete order sequence: ${error.message}`);
    }
  };

  // CRUD Handlers for oexptimeline (Order Exception Sequence)
  const handleEditExceptionSequence = (item) => {
    setEditingExceptionSequenceId(item.id);
    setEditExceptionSequenceData({
      id: item.id,
      requestState: item.requestState,
      showState1: item.showState1,
      showState2: item.showState2
    });
  };

  const handleSaveExceptionSequence = async (id) => {
    try {
      const data = editExceptionSequenceData.id.startsWith('stop')
        ? [editExceptionSequenceData.requestState, editExceptionSequenceData.showState1]
        : [editExceptionSequenceData.requestState, editExceptionSequenceData.showState1, editExceptionSequenceData.showState2];
      await writeCombination('oexptimeline', data, editExceptionSequenceData.id);
      setEditingExceptionSequenceId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving exception sequence:', error);
      showAlert(`Failed to save exception sequence: ${error.message}`);
    }
  };

  const handleAddExceptionSequence = async () => {
    try {
      if (!newExceptionSequenceData.id) {
        throw new Error('Key (ID) is required in the format change<number>, option<number>, or stop<number>.');
      }
      if (!newExceptionSequenceData.requestState || !newExceptionSequenceData.showState1) {
        throw new Error('Request State and Show State1 are required.');
      }
      if (!newExceptionSequenceData.id.startsWith('stop') && !newExceptionSequenceData.showState2) {
        throw new Error('Show State2 is required for change or option keys.');
      }
      const controlId = newExceptionSequenceData.id.trim();
      if (controlId.includes('/')) {
        throw new Error('Key (ID) cannot contain slashes (/). Please choose a different ID.');
      }
      if (!controlId.match(/^(change|option|stop)\d+$/)) {
        throw new Error('Key (ID) must be in the format "change<number>", "option<number>", or "stop<number>" (e.g., change1, option2, stop3).');
      }
      const existingExceptionSequence = exceptionSequences.find(control => control.id === controlId);
      if (existingExceptionSequence) {
        throw new Error(`Key (ID) "${controlId}" already exists in oexptimeline. Please choose a different ID.`);
      }
      const data = controlId.startsWith('stop')
        ? [newExceptionSequenceData.requestState, newExceptionSequenceData.showState1]
        : [newExceptionSequenceData.requestState, newExceptionSequenceData.showState1, newExceptionSequenceData.showState2];
      await writeCombination('oexptimeline', data, controlId);
      setAddingExceptionSequence(false);
      setNewExceptionSequenceData({ id: '', requestState: '', showState1: '', showState2: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding exception sequence:', error.message, error.stack);
      showAlert(`Failed to add exception sequence: ${error.message}`);
    }
  };

  const handleDeleteExceptionSequence = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this exception sequence?'))) return;
    try {
      await deleteTemplateRecord('oexptimeline', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting exception sequence:', error);
      showAlert(`Failed to delete exception sequence: ${error.message}`);
    }
  };

  // CRUD Handlers for oprscontrol (Order Process Control)
  const handleEditProcessControl = (item) => {
    setEditingProcessControlId(item.id);
    setEditProcessControlData({
      id: item.id,
      application: item.application,
      activity: item.activity,
      state: item.state
    });
  };

  const handleSaveProcessControl = async (oldId) => {
    try {
      const newId = editProcessControlData.id.trim();
      await writeCombination('oprscontrol', [
        editProcessControlData.application,
        editProcessControlData.activity,
        editProcessControlData.state
      ], newId);
      // If the ID was renamed, delete the old document
      if (newId !== oldId) {
        await deleteTemplateRecord('oprscontrol', oldId);
      }
      setEditingProcessControlId(null);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error saving process control:', error);
      showAlert(`Failed to save process control: ${error.message}`);
    }
  };

  const handleAddProcessControl = async () => {
    try {
      if (!newProcessControlData.id) {
        throw new Error('Key (ID) is required in the format buyer<number>, super<number>, admin<number>, or mover<number>.');
      }
      if (!newProcessControlData.application || !newProcessControlData.activity || !newProcessControlData.state) {
        throw new Error('All fields (Application, Activity, State) are required.');
      }
      const controlId = newProcessControlData.id.trim();
      if (controlId.includes('/')) {
        throw new Error('Key (ID) cannot contain slashes (/). Please choose a different ID.');
      }
      // Updated to include mover prefix
      if (!controlId.match(/^(buyer|super|admin|mover)\d+$/)) {
        throw new Error('Key (ID) must be in the format "buyer<number>", "super<number>", "admin<number>", or "mover<number>" (e.g., buyer1, super2, admin3, mover1).');
      }
      const existingProcessControl = processControls.find(control => control.id === controlId);
      if (existingProcessControl) {
        throw new Error(`Key (ID) "${controlId}" already exists in oprscontrol. Please choose a different ID.`);
      }
      await writeCombination('oprscontrol', [
        newProcessControlData.application,
        newProcessControlData.activity,
        newProcessControlData.state
      ], controlId);
      setAddingProcessControl(false);
      setNewProcessControlData({ id: '', application: '', activity: '', state: '' });
      fetchCombinationsData();
    } catch (error) {
      console.error('Error adding process control:', error.message, error.stack);
      showAlert(`Failed to add process control: ${error.message}`);
    }
  };

  const handleDeleteProcessControl = async (id) => {
    if (!(await showConfirm('Are you sure you want to delete this process control?'))) return;
    try {
      await deleteTemplateRecord('oprscontrol', id);
      fetchCombinationsData();
    } catch (error) {
      console.error('Error deleting process control:', error);
      showAlert(`Failed to delete process control: ${error.message}`);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!user) return <div>Please log in to access this page.</div>;
  if (userRole !== 'Admin' && userRole !== 'Super') return null;

  return (
    <>
      <style>
        {`
          /* Adjust size for smaller screens */
          @media (max-width: 600px) {
            .page-body .meta-table th,
            .page-body .meta-table td {
              padding: 2px;
              font-size: 8px;
            }
  
            .action-btn.delete-btn {
              padding: 2px 4px;
              font-size: 8px;
            }
  
            .filter-input {
              padding: 2px;
              font-size: 10px;
            }
  
            h4 {
              font-size: 12px;
              margin: 4px 0;
            }
  
            .filter-section {
              width: 60%;
              margin-top: 15px;
            }
  
            .search-clear-btn {
              font-size: 8px;
              padding: 1px;
            }
  
            .no-match-message {
              font-size: 8px;
            }
  
            .add-btn {
              font-size: 10px;
              margin-top: 3px;
            }

            .toggle-btn {
              font-size: 10px;
            }

            .table-header {
              font-size: 10px;
            }
          }
  
          .page-body {
            display: flex;
            flex-direction: column;
            gap: 20px;
            padding: 10px;
            width: 100%;
            box-sizing: border-box;
          }
  
          .filter-section {
            display: flex;
            gap: 5px;
            margin-bottom: 10px;
            width: 60%;
            margin: 10px auto;
            position: relative;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            background: #fff;
          }
  
          .filter-input {
            padding: 4px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 12px;
            flex: 1;
          }
  
          .search-clear-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: #666;
            font-size: 10px;
            padding: 2px;
            position: absolute;
            right: 5px;
            top: 50%;
            transform: translateY(-50%);
          }
  
          .search-clear-btn:hover {
            color: #333;
          }
  
          /* Combined Horizontal Scroll Container */
          .tables-scroll-container {
            overflow-x: auto;
            width: 100%;
          }
  
          /* Table Containers for Vertical Scrolling */
          .table-container {
            overflow-y: auto;
            max-height: 40vh;
            width: 100%;
            transition: max-height 0.3s ease;
          }

          .table-container.shrunk {
            max-height: 0;
            overflow: hidden;
          }

          .table-header {
            display: flex;
            align-items: center;
            gap: 5px;
            margin: 10px 0 5px 0;
          }

          .toggle-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 12px;
            color: #666;
          }

          .toggle-btn:hover {
            color: #333;
          }
  
          .page-body .meta-table {
            width: 100%;
            min-width: 300px; /* Reduced min-width */
            border-collapse: collapse;
            table-layout: auto; /* Columns size based on content */
            background-color: #fff;
          }
  
          .page-body .meta-table th,
          .page-body .meta-table td {
            padding: 4px; /* Reduced padding */
            border: 1px solid #ddd;
            text-align: left;
            font-size: 10px;
            line-height: 1.2;
          }
  
          .page-body .meta-table th {
            background-color: #f4f4f4;
            font-weight: bold;
            position: sticky;
            top: 0;
            z-index: 1;
          }
  
          .page-body .meta-table td {
            background-color: #fff;
            color: #555;
            overflow: hidden;
            white-space: normal; /* Allow text wrapping */
          }
  
          .sort-icon {
            display: inline-block;
            margin-left: 2px;
            cursor: pointer;
            vertical-align: middle;
            font-size: 10px;
          }
  
          .meta-table input {
            padding: 2px;
            border: 1px solid #ddd;
            border-radius: 3px;
            width: 100%;
            font-size: 10px;
          }
  
          /* Action Buttons */
          .action-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 10px; /* Reduced size */
            padding: 1px;
            margin: 0 1px;
            color: #666;
            transition: color 0.2s;
          }
  
          .action-btn:hover {
            color: #333;
          }
  
          .action-btn:disabled {
            color: #999;
            cursor: not-allowed;
          }
  
          .edit-btn,
          .delete-btn {
            color: #666;
          }
  
          .edit-btn:hover,
          .delete-btn:hover {
            color: #333;
          }
  
          .edit-btn:disabled,
          .delete-btn:disabled {
            color: #999;
          }
  
          .delete-btn {
            border-radius: 5px;
            padding: 1px 2px;
          }
  
          .action-buttons {
            display: flex;
            justify-content: center;
            gap: 2px;
          }
  
          /* Add Button */
          .add-btn {
            background: none;
            border: none;
            cursor: pointer;
            color: #666;
            font-size: 10px;
            margin-top: 5px;
            display: flex;
            align-items: center;
            gap: 2px;
          }
  
          .add-btn:hover {
            color: #333;
          }
  
          /* No Match Message */
          .no-match-message {
            color: #666;
            font-size: 10px;
            margin: 8px 0;
          }
  
          /* Close button */
          .meta-close-btn {
            position: absolute;
            top: 10px;
            right: 10px;
            background: none;
            border: none;
            font-size: 18px;
            cursor: pointer;
            color: #666;
            z-index: 10;
            padding: 5px;
            line-height: 1;
          }

          .meta-close-btn:hover {
            color: #333;
          }

          /* Shared Styles */
          .meta-page {
            display: flex;
            flex-direction: column;
            padding: 5px;
            padding-bottom: 50px;
            background-color: #f5f5f5;
            width: 100%;
            box-sizing: border-box;
            position: relative;
          }
  
          h4 {
            font-size: 12px;
            margin: 6px 0;
            color: #444;
            font-weight: 500;
          }
  
          input {
            padding: 4px;
            border: 1px solid #ddd;
            border-radius: 5px;
            font-size: 10px;
            width: 100%;
            box-sizing: border-box;
            white-space: normal;
          }
        `}
      </style>
  
      <div className="meta-page">
        <button className="meta-close-btn" onClick={handleClose} title="Close">
          <FaTimes />
        </button>
        <div className="page-body">
          <div className="filter-section">
            <input
              type="text"
              className="filter-input"
              placeholder="Search across all fields..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={handleClearSearch}>
                <FaTimes />
              </button>
            )}
          </div>
  
          {userRole === 'Super' ? (
            <div className="tables-scroll-container">
              {/* Flavours & Fillings Table */}
              <div>
                <div className="table-header">
                  <h4>Flavours & Fillings</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsFlavoursExpanded(!isFlavoursExpanded)}
                  >
                    {isFlavoursExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isFlavoursExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table flavours-fillings">
                    <thead>
                      <tr>
                        <th>
                          Flavour
                          <div className="sort-icon" onClick={() => handleSort('flavour', setCombinationSortConfig, applyCombinationFiltersAndSort, combinations)}>
                            {combinationSortConfig.key === 'flavour' ? (
                              combinationSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Filling
                          <div className="sort-icon" onClick={() => handleSort('filling', setCombinationSortConfig, applyCombinationFiltersAndSort, combinations)}>
                            {combinationSortConfig.key === 'filling' ? (
                              combinationSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Sponge
                          <div className="sort-icon" onClick={() => handleSort('sponge', setCombinationSortConfig, applyCombinationFiltersAndSort, combinations)}>
                            {combinationSortConfig.key === 'sponge' ? (
                              combinationSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Segment
                          <div className="sort-icon" onClick={() => handleSort('segment', setCombinationSortConfig, applyCombinationFiltersAndSort, combinations)}>
                            {combinationSortConfig.key === 'segment' ? (
                              combinationSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Price Segment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCombinations.map(item => (
                        <tr key={item.id}>
                          <td>{editingCombinationId === item.id ? (
                            <input
                              type="text"
                              value={editCombinationData.flavour}
                              onChange={(e) => setEditCombinationData({ ...editCombinationData, flavour: e.target.value })}
                            />
                          ) : (
                            item.flavour
                          )}</td>
                          <td>{editingCombinationId === item.id ? (
                            <input
                              type="text"
                              value={editCombinationData.filling}
                              onChange={(e) => setEditCombinationData({ ...editCombinationData, filling: e.target.value })}
                            />
                          ) : (
                            item.filling
                          )}</td>
                          <td>{editingCombinationId === item.id ? (
                            <input
                              type="text"
                              value={editCombinationData.sponge}
                              onChange={(e) => setEditCombinationData({ ...editCombinationData, sponge: e.target.value })}
                            />
                          ) : (
                            item.sponge.join(', ')
                          )}</td>
                          <td>{editingCombinationId === item.id ? (
                            <input
                              type="text"
                              value={editCombinationData.segment}
                              onChange={(e) => setEditCombinationData({ ...editCombinationData, segment: e.target.value })}
                            />
                          ) : (
                            item.segment
                          )}</td>
                          <td>{editingCombinationId === item.id ? (
                            <select
                              value={editCombinationData.priceSegment}
                              onChange={(e) => setEditCombinationData({ ...editCombinationData, priceSegment: e.target.value })}
                            >
                              <option value="">--</option>
                              <option value="N">N</option>
                              <option value="S">S</option>
                            </select>
                          ) : (
                            item.priceSegment
                          )}</td>
                          <td className="action-cell">
                            {editingCombinationId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveCombination(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingCombinationId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditCombination(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteCombination(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingCombination && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newCombinationData.flavour}
                              onChange={(e) => setNewCombinationData({ ...newCombinationData, flavour: e.target.value })}
                              placeholder="Flavour"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newCombinationData.filling}
                              onChange={(e) => setNewCombinationData({ ...newCombinationData, filling: e.target.value })}
                              placeholder="Filling"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newCombinationData.sponge}
                              onChange={(e) => setNewCombinationData({ ...newCombinationData, sponge: e.target.value })}
                              placeholder="Sponge"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newCombinationData.segment}
                              onChange={(e) => setNewCombinationData({ ...newCombinationData, segment: e.target.value })}
                              placeholder="Segment"
                            />
                          </td>
                          <td>
                            <select
                              value={newCombinationData.priceSegment}
                              onChange={(e) => setNewCombinationData({ ...newCombinationData, priceSegment: e.target.value })}
                            >
                              <option value="">--</option>
                              <option value="N">N</option>
                              <option value="S">S</option>
                            </select>
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddCombination}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingCombination(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isFlavoursExpanded && (
                    <button className="add-btn" onClick={() => setAddingCombination(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredCombinations.length === 0 && !addingCombination && isFlavoursExpanded && (
                  <p className="no-match-message">No flavours & fillings match the current search.</p>
                )}
              </div>
  
              {/* Covers Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Covers</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsCoversExpanded(!isCoversExpanded)}
                  >
                    {isCoversExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isCoversExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table covers">
                    <thead>
                      <tr>
                        <th>
                          Cover
                          <div className="sort-icon" onClick={() => handleSort('cover', setCoverCombiSortConfig, applyCoverCombiFiltersAndSort, coverCombis)}>
                            {coverCombiSortConfig.key === 'cover' ? (
                              coverCombiSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Frosting
                          <div className="sort-icon" onClick={() => handleSort('frosting', setCoverCombiSortConfig, applyCoverCombiFiltersAndSort, coverCombis)}>
                            {coverCombiSortConfig.key === 'frosting' ? (
                              coverCombiSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Price Segment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCoverCombis.map(item => (
                        <tr key={item.id}>
                          <td>{editingCoverCombiId === item.id ? (
                            <input
                              type="text"
                              value={editCoverCombiData.cover}
                              onChange={(e) => setEditCoverCombiData({ ...editCoverCombiData, cover: e.target.value })}
                            />
                          ) : (
                            item.cover
                          )}</td>
                          <td>{editingCoverCombiId === item.id ? (
                            <input
                              type="text"
                              value={editCoverCombiData.frosting}
                              onChange={(e) => setEditCoverCombiData({ ...editCoverCombiData, frosting: e.target.value })}
                            />
                          ) : (
                            item.frosting
                          )}</td>
                          <td>{editingCoverCombiId === item.id ? (
                            <select
                              value={editCoverCombiData.priceSegment}
                              onChange={(e) => setEditCoverCombiData({ ...editCoverCombiData, priceSegment: e.target.value })}
                            >
                              <option value="">--</option>
                              <option value="F">F</option>
                              <option value="B">B</option>
                            </select>
                          ) : (
                            item.priceSegment
                          )}</td>
                          <td className="action-cell">
                            {editingCoverCombiId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveCoverCombi(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingCoverCombiId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditCoverCombi(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteCoverCombi(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingCoverCombi && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newCoverCombiData.cover}
                              onChange={(e) => setNewCoverCombiData({ ...newCoverCombiData, cover: e.target.value })}
                              placeholder="Cover"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newCoverCombiData.frosting}
                              onChange={(e) => setNewCoverCombiData({ ...newCoverCombiData, frosting: e.target.value })}
                              placeholder="Frosting"
                            />
                          </td>
                          <td>
                            <select
                              value={newCoverCombiData.priceSegment}
                              onChange={(e) => setNewCoverCombiData({ ...newCoverCombiData, priceSegment: e.target.value })}
                            >
                              <option value="">--</option>
                              <option value="F">F</option>
                              <option value="B">B</option>
                            </select>
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddCoverCombi}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingCoverCombi(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isCoversExpanded && (
                    <button className="add-btn" onClick={() => setAddingCoverCombi(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredCoverCombis.length === 0 && !addingCoverCombi && isCoversExpanded && (
                  <p className="no-match-message">No covers match the current search.</p>
                )}
              </div>
  
              {/* Events Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Events</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsEventsExpanded(!isEventsExpanded)}
                  >
                    {isEventsExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isEventsExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table events">
                    <thead>
                      <tr>
                        <th>
                          ID
                          <div className="sort-icon" onClick={() => handleSort('id', setEventSortConfig, applyEventFiltersAndSort, events)}>
                            {eventSortConfig.key === 'id' ? (
                              eventSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Name
                          <div className="sort-icon" onClick={() => handleSort('name', setEventSortConfig, applyEventFiltersAndSort, events)}>
                            {eventSortConfig.key === 'name' ? (
                              eventSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvents.map(item => (
                        <tr key={item.id}>
                          <td>{editingEventId === item.id ? (
                            <input
                              type="text"
                              value={editEventData.id}
                              onChange={(e) => setEditEventData({ ...editEventData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingEventId === item.id ? (
                            <input
                              type="text"
                              value={editEventData.name}
                              onChange={(e) => setEditEventData({ ...editEventData, name: e.target.value })}
                            />
                          ) : (
                            item.name
                          )}</td>
                          <td className="action-cell">
                            {editingEventId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveEvent(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingEventId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditEvent(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteEvent(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingEvent && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newEventData.id}
                              onChange={(e) => setNewEventData({ ...newEventData, id: e.target.value })}
                              placeholder="ID"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newEventData.name}
                              onChange={(e) => setNewEventData({ ...newEventData, name: e.target.value })}
                              placeholder="Event Name"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddEvent}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingEvent(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isEventsExpanded && (
                    <button className="add-btn" onClick={() => setAddingEvent(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredEvents.length === 0 && !addingEvent && isEventsExpanded && (
                  <p className="no-match-message">No events match the current search.</p>
                )}
              </div>
  
              {/* Categories Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Categories</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsShapesExpanded(!isShapesExpanded)}
                  >
                    {isShapesExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isShapesExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table shapes">
                    <thead>
                      <tr>
                        <th>
                          ID
                          <div className="sort-icon" onClick={() => handleSort('id', setShapeSortConfig, applyShapeFiltersAndSort, shapes)}>
                            {shapeSortConfig.key === 'id' ? (
                              shapeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Name
                          <div className="sort-icon" onClick={() => handleSort('name', setShapeSortConfig, applyShapeFiltersAndSort, shapes)}>
                            {shapeSortConfig.key === 'name' ? (
                              shapeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredShapes.map(item => (
                        <tr key={item.id}>
                          <td>{editingShapeId === item.id ? (
                            <input
                              type="text"
                              value={editShapeData.id}
                              onChange={(e) => setEditShapeData({ ...editShapeData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingShapeId === item.id ? (
                            <input
                              type="text"
                              value={editShapeData.name}
                              onChange={(e) => setEditShapeData({ ...editShapeData, name: e.target.value })}
                            />
                          ) : (
                            item.name
                          )}</td>
                          <td className="action-cell">
                            {editingShapeId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveShape(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingShapeId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditShape(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteShape(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingShape && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newShapeData.id}
                              onChange={(e) => setNewShapeData({ ...newShapeData, id: e.target.value })}
                              placeholder="ID"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newShapeData.name}
                              onChange={(e) => setNewShapeData({ ...newShapeData, name: e.target.value })}
                              placeholder="Shape Name"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddShape}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingShape(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isShapesExpanded && (
                    <button className="add-btn" onClick={() => setAddingShape(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredShapes.length === 0 && !addingShape && isShapesExpanded && (
                  <p className="no-match-message">No shapes match the current search.</p>
                )}
              </div>

              {/* Holiday Calendar Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Holiday Calendar</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsHolidayCalendarExpanded(!isHolidayCalendarExpanded)}
                  >
                    {isHolidayCalendarExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isHolidayCalendarExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table holiday-calendars">
                    <thead>
                      <tr>
                        <th>
                          ID
                          <div className="sort-icon" onClick={() => handleSort('id', setHolidayCalendarSortConfig, applyHolidayCalendarFiltersAndSort, holidayCalendars)}>
                            {holidayCalendarSortConfig.key === 'id' ? (
                              holidayCalendarSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Day
                          <div className="sort-icon" onClick={() => handleSort('name', setHolidayCalendarSortConfig, applyHolidayCalendarFiltersAndSort, holidayCalendars)}>
                            {holidayCalendarSortConfig.key === 'name' ? (
                              holidayCalendarSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHolidayCalendars.map(item => (
                        <tr key={item.id}>
                          <td>{editingHolidayCalendarId === item.id ? (
                            <input
                              type="text"
                              value={editHolidayCalendarData.id}
                              onChange={(e) => setEditHolidayCalendarData({ ...editHolidayCalendarData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingHolidayCalendarId === item.id ? (
                            <input
                              type="date"
                              value={editHolidayCalendarData.day}
                              onChange={(e) => setEditHolidayCalendarData({ ...editHolidayCalendarData, day: e.target.value })}
                            />
                          ) : (
                            item.name
                          )}</td>
                          <td className="action-cell">
                            {editingHolidayCalendarId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveHolidayCalendar(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingHolidayCalendarId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditHolidayCalendar(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteHolidayCalendar(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingHolidayCalendar && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newHolidayCalendarData.id}
                              onChange={(e) => setNewHolidayCalendarData({ ...newHolidayCalendarData, id: e.target.value })}
                              placeholder="ID (e.g., holiday1, offday2)"
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              value={newHolidayCalendarData.day}
                              onChange={(e) => setNewHolidayCalendarData({ ...newHolidayCalendarData, day: e.target.value })}
                              placeholder="Day"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddHolidayCalendar}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingHolidayCalendar(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isHolidayCalendarExpanded && (
                    <button className="add-btn" onClick={() => setAddingHolidayCalendar(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredHolidayCalendars.length === 0 && !addingHolidayCalendar && isHolidayCalendarExpanded && (
                  <p className="no-match-message">No holiday calendar entries match the current search.</p>
                )}
              </div>
              {/* Variant Charges Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Variant Charges</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsVariantChargesExpanded(!isVariantChargesExpanded)}
                  >
                    {isVariantChargesExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isVariantChargesExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table">
                    <thead>
                      <tr>
                        <th>
                          Variant
                          <div className="sort-icon" onClick={() => handleSort('variant', setVariantChargeSortConfig, applyVariantChargeFiltersAndSort, variantCharges)}>
                            {variantChargeSortConfig.key === 'variant' ? (
                              variantChargeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          {`Cost (${getCurrencySymbol()})`}
                          <div className="sort-icon" onClick={() => handleSort('cost', setVariantChargeSortConfig, applyVariantChargeFiltersAndSort, variantCharges)}>
                            {variantChargeSortConfig.key === 'cost' ? (
                              variantChargeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Person Served</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVariantCharges.map(item => (
                        <tr key={item.id}>
                          <td>{editingVariantChargeId === item.id ? (
                            <input
                              type="text"
                              value={editVariantChargeData.variant}
                              onChange={(e) => setEditVariantChargeData({ ...editVariantChargeData, variant: e.target.value })}
                              placeholder="e.g. T10, 3T5"
                            />
                          ) : (
                            item.variant
                          )}</td>
                          <td>{editingVariantChargeId === item.id ? (
                            <input
                              type="text"
                              value={editVariantChargeData.cost}
                              onChange={(e) => setEditVariantChargeData({ ...editVariantChargeData, cost: e.target.value })}
                              placeholder="0.00"
                            />
                          ) : (
                            item.cost ? `${getCurrencySymbol()}${item.cost}` : ''
                          )}</td>
                          <td>{editingVariantChargeId === item.id ? (
                            <input
                              type="text"
                              value={editVariantChargeData.personServed}
                              onChange={(e) => setEditVariantChargeData({ ...editVariantChargeData, personServed: e.target.value })}
                              placeholder="e.g. 10"
                            />
                          ) : (
                            item.personServed || ''
                          )}</td>
                          <td className="action-cell">
                            {editingVariantChargeId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveVariantCharge(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingVariantChargeId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditVariantCharge(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteVariantCharge(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingVariantCharge && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newVariantChargeData.variant}
                              onChange={(e) => setNewVariantChargeData({ ...newVariantChargeData, variant: e.target.value })}
                              placeholder="e.g. T10, 3T5"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newVariantChargeData.cost}
                              onChange={(e) => setNewVariantChargeData({ ...newVariantChargeData, cost: e.target.value })}
                              placeholder="0.00"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newVariantChargeData.personServed}
                              onChange={(e) => setNewVariantChargeData({ ...newVariantChargeData, personServed: e.target.value })}
                              placeholder="e.g. 10"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddVariantCharge}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingVariantCharge(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isVariantChargesExpanded && (
                    <button className="add-btn" onClick={() => setAddingVariantCharge(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredVariantCharges.length === 0 && !addingVariantCharge && isVariantChargesExpanded && (
                  <p className="no-match-message">No variant charges match the current search.</p>
                )}
              </div>
              {/* Delivery Timewindow Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Delivery Timewindow</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsTimewindowExpanded(!isTimewindowExpanded)}
                  >
                    {isTimewindowExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isTimewindowExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table timewindows">
                    <thead>
                      <tr>
                        <th>
                          ID
                          <div className="sort-icon" onClick={() => handleSort('id', setTimewindowSortConfig, applyTimewindowFiltersAndSort, timewindows)}>
                            {timewindowSortConfig.key === 'id' ? (
                              timewindowSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Window
                          <div className="sort-icon" onClick={() => handleSort('name', setTimewindowSortConfig, applyTimewindowFiltersAndSort, timewindows)}>
                            {timewindowSortConfig.key === 'name' ? (
                              timewindowSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTimewindows.map(item => (
                        <tr key={item.id}>
                          <td>{editingTimewindowId === item.id ? (
                            <input
                              type="text"
                              value={editTimewindowData.id}
                              onChange={(e) => setEditTimewindowData({ ...editTimewindowData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingTimewindowId === item.id ? (
                            <input
                              type="text"
                              value={editTimewindowData.window}
                              onChange={(e) => setEditTimewindowData({ ...editTimewindowData, window: e.target.value })}
                              placeholder="e.g., 09 Hour - 12 Hour"
                            />
                          ) : (
                            item.name
                          )}</td>
                          <td className="action-cell">
                            {editingTimewindowId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveTimewindow(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingTimewindowId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditTimewindow(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteTimewindow(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingTimewindow && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newTimewindowData.id}
                              onChange={(e) => setNewTimewindowData({ ...newTimewindowData, id: e.target.value })}
                              placeholder="ID (e.g., weekdays1, weekend2)"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newTimewindowData.window}
                              onChange={(e) => setNewTimewindowData({ ...newTimewindowData, window: e.target.value })}
                              placeholder="e.g., 09 Hour - 12 Hour"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddTimewindow}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingTimewindow(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isTimewindowExpanded && (
                    <button className="add-btn" onClick={() => setAddingTimewindow(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredTimewindows.length === 0 && !addingTimewindow && isTimewindowExpanded && (
                  <p className="no-match-message">No timewindows match the current search.</p>
                )}
              </div>

              {/* Delivery Charges Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Delivery Charges</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsDeliveryChargesExpanded(!isDeliveryChargesExpanded)}
                  >
                    {isDeliveryChargesExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isDeliveryChargesExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table delivery-charges">
                    <thead>
                      <tr>
                        <th>
                          Distance (km)
                          <div className="sort-icon" onClick={() => handleSort('distance', setDeliveryChargeSortConfig, applyDeliveryChargeFiltersAndSort, deliveryCharges)}>
                            {deliveryChargeSortConfig.key === 'distance' ? (
                              deliveryChargeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          {`Cost (${getCurrencySymbol()})`}
                          <div className="sort-icon" onClick={() => handleSort('cost', setDeliveryChargeSortConfig, applyDeliveryChargeFiltersAndSort, deliveryCharges)}>
                            {deliveryChargeSortConfig.key === 'cost' ? (
                              deliveryChargeSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeliveryCharges.map(item => (
                        <tr key={item.id}>
                          <td>{editingDeliveryChargeId === item.id ? (
                            <input
                              type="text"
                              value={editDeliveryChargeData.distance}
                              onChange={(e) => setEditDeliveryChargeData({ ...editDeliveryChargeData, distance: e.target.value })}
                            />
                          ) : (
                            item.distance
                          )}</td>
                          <td>{editingDeliveryChargeId === item.id ? (
                            <input
                              type="text"
                              value={editDeliveryChargeData.cost}
                              onChange={(e) => setEditDeliveryChargeData({ ...editDeliveryChargeData, cost: e.target.value })}
                            />
                          ) : (
                            `${getCurrencySymbol()}${item.cost}`
                          )}</td>
                          <td className="action-cell">
                            {editingDeliveryChargeId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveDeliveryCharge(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingDeliveryChargeId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditDeliveryCharge(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteDeliveryCharge(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingDeliveryCharge && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newDeliveryChargeData.distance}
                              onChange={(e) => setNewDeliveryChargeData({ ...newDeliveryChargeData, distance: e.target.value })}
                              placeholder="Distance (km)"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newDeliveryChargeData.cost}
                              onChange={(e) => setNewDeliveryChargeData({ ...newDeliveryChargeData, cost: e.target.value })}
                              placeholder="Cost (€)"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddDeliveryCharge}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingDeliveryCharge(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isDeliveryChargesExpanded && (
                    <button className="add-btn" onClick={() => setAddingDeliveryCharge(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredDeliveryCharges.length === 0 && !addingDeliveryCharge && isDeliveryChargesExpanded && (
                  <p className="no-match-message">No delivery charges match the current search.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="tables-scroll-container">
              {/* Order Sequence Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Order Sequence</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsOrderSequenceExpanded(!isOrderSequenceExpanded)}
                  >
                    {isOrderSequenceExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isOrderSequenceExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table order-sequences">
                    <thead>
                      <tr>
                        <th>
                          ID
                          <div className="sort-icon" onClick={() => handleSort('id', setOrderSequenceSortConfig, applyOrderSequenceFiltersAndSort, orderSequences)}>
                            {orderSequenceSortConfig.key === 'id' ? (
                              orderSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Name
                          <div className="sort-icon" onClick={() => handleSort('name', setOrderSequenceSortConfig, applyOrderSequenceFiltersAndSort, orderSequences)}>
                            {orderSequenceSortConfig.key === 'name' ? (
                              orderSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrderSequences.map(item => (
                        <tr key={item.id}>
                          <td>{editingOrderSequenceId === item.id ? (
                            <input
                              type="text"
                              value={editOrderSequenceData.id}
                              onChange={(e) => setEditOrderSequenceData({ ...editOrderSequenceData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingOrderSequenceId === item.id ? (
                            <input
                              type="text"
                              value={editOrderSequenceData.name}
                              onChange={(e) => setEditOrderSequenceData({ ...editOrderSequenceData, name: e.target.value })}
                            />
                          ) : (
                            item.name
                          )}</td>
                          <td className="action-cell">
                            {editingOrderSequenceId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveOrderSequence(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingOrderSequenceId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditOrderSequence(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteOrderSequence(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingOrderSequence && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newOrderSequenceData.id}
                              onChange={(e) => setNewOrderSequenceData({ ...newOrderSequenceData, id: e.target.value })}
                              placeholder="ID"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newOrderSequenceData.name}
                              onChange={(e) => setNewOrderSequenceData({ ...newOrderSequenceData, name: e.target.value })}
                              placeholder="Order Sequence Name"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddOrderSequence}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingOrderSequence(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isOrderSequenceExpanded && (
                    <button className="add-btn" onClick={() => setAddingOrderSequence(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredOrderSequences.length === 0 && !addingOrderSequence && isOrderSequenceExpanded && (
                  <p className="no-match-message">No order sequences match the current search.</p>
                )}
              </div>
  
              {/* Order Exception Sequence Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Order Exception Sequence</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsExceptionSequenceExpanded(!isExceptionSequenceExpanded)}
                  >
                    {isExceptionSequenceExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isExceptionSequenceExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table exception-sequences">
                    <thead>
                      <tr>
                        <th>
                          Key
                          <div className="sort-icon" onClick={() => handleSort('id', setExceptionSequenceSortConfig, applyExceptionSequenceFiltersAndSort, exceptionSequences)}>
                            {exceptionSequenceSortConfig.key === 'id' ? (
                              exceptionSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Request State
                          <div className="sort-icon" onClick={() => handleSort('requestState', setExceptionSequenceSortConfig, applyExceptionSequenceFiltersAndSort, exceptionSequences)}>
                            {exceptionSequenceSortConfig.key === 'requestState' ? (
                              exceptionSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Show State1
                          <div className="sort-icon" onClick={() => handleSort('showState1', setExceptionSequenceSortConfig, applyExceptionSequenceFiltersAndSort, exceptionSequences)}>
                            {exceptionSequenceSortConfig.key === 'showState1' ? (
                              exceptionSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Show State2
                          <div className="sort-icon" onClick={() => handleSort('showState2', setExceptionSequenceSortConfig, applyExceptionSequenceFiltersAndSort, exceptionSequences)}>
                            {exceptionSequenceSortConfig.key === 'showState2' ? (
                              exceptionSequenceSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExceptionSequences.map(item => (
                        <tr key={item.id}>
                          <td>{editingExceptionSequenceId === item.id ? (
                            <input
                              type="text"
                              value={editExceptionSequenceData.id}
                              onChange={(e) => setEditExceptionSequenceData({ ...editExceptionSequenceData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingExceptionSequenceId === item.id ? (
                            <input
                              type="text"
                              value={editExceptionSequenceData.requestState}
                              onChange={(e) => setEditExceptionSequenceData({ ...editExceptionSequenceData, requestState: e.target.value })}
                            />
                          ) : (
                            item.requestState
                          )}</td>
                          <td>{editingExceptionSequenceId === item.id ? (
                            <input
                              type="text"
                              value={editExceptionSequenceData.showState1}
                              onChange={(e) => setEditExceptionSequenceData({ ...editExceptionSequenceData, showState1: e.target.value })}
                            />
                          ) : (
                            item.showState1
                          )}</td>
                          <td>{editingExceptionSequenceId === item.id ? (
                            item.id.startsWith('stop') ? '-' : (
                              <input
                                type="text"
                                value={editExceptionSequenceData.showState2}
                                onChange={(e) => setEditExceptionSequenceData({ ...editExceptionSequenceData, showState2: e.target.value })}
                              />
                            )
                          ) : (
                            item.showState2 || '-'
                          )}</td>
                          <td className="action-cell">
                            {editingExceptionSequenceId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveExceptionSequence(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingExceptionSequenceId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditExceptionSequence(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteExceptionSequence(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingExceptionSequence && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newExceptionSequenceData.id}
                              onChange={(e) => setNewExceptionSequenceData({ ...newExceptionSequenceData, id: e.target.value })}
                              placeholder="ID (e.g., change1, option2, stop3)"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newExceptionSequenceData.requestState}
                              onChange={(e) => setNewExceptionSequenceData({ ...newExceptionSequenceData, requestState: e.target.value })}
                              placeholder="Request State"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newExceptionSequenceData.showState1}
                              onChange={(e) => setNewExceptionSequenceData({ ...newExceptionSequenceData, showState1: e.target.value })}
                              placeholder="Show State1"
                            />
                          </td>
                          <td>
                            {newExceptionSequenceData.id.startsWith('stop') ? '-' : (
                              <input
                                type="text"
                                value={newExceptionSequenceData.showState2}
                                onChange={(e) => setNewExceptionSequenceData({ ...newExceptionSequenceData, showState2: e.target.value })}
                                placeholder="Show State2"
                              />
                            )}
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddExceptionSequence}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingExceptionSequence(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isExceptionSequenceExpanded && (
                    <button className="add-btn" onClick={() => setAddingExceptionSequence(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredExceptionSequences.length === 0 && !addingExceptionSequence && isExceptionSequenceExpanded && (
                  <p className="no-match-message">No exception sequences match the current search.</p>
                )}
              </div>
  
              {/* Order Process Control Table */}
              <div style={{ margin: '10px 0 0 0' }}>
                <div className="table-header">
                  <h4>Order Process Control</h4>
                  <button
                    className="toggle-btn"
                    onClick={() => setIsProcessControlExpanded(!isProcessControlExpanded)}
                  >
                    {isProcessControlExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </div>
                <div className={`table-container ${isProcessControlExpanded ? '' : 'shrunk'}`}>
                  <table className="meta-table process-controls">
                    <thead>
                      <tr>
                        <th>
                          Key
                          <div className="sort-icon" onClick={() => handleSort('id', setProcessControlSortConfig, applyProcessControlFiltersAndSort, processControls)}>
                            {processControlSortConfig.key === 'id' ? (
                              processControlSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Application
                          <div className="sort-icon" onClick={() => handleSort('application', setProcessControlSortConfig, applyProcessControlFiltersAndSort, processControls)}>
                            {processControlSortConfig.key === 'application' ? (
                              processControlSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          Activity
                          <div className="sort-icon" onClick={() => handleSort('activity', setProcessControlSortConfig, applyProcessControlFiltersAndSort, processControls)}>
                            {processControlSortConfig.key === 'activity' ? (
                              processControlSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>
                          State
                          <div className="sort-icon" onClick={() => handleSort('state', setProcessControlSortConfig, applyProcessControlFiltersAndSort, processControls)}>
                            {processControlSortConfig.key === 'state' ? (
                              processControlSortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                            ) : (
                              <FaSort />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProcessControls.map(item => (
                        <tr key={item.id}>
                          <td>{editingProcessControlId === item.id ? (
                            <input
                              type="text"
                              value={editProcessControlData.id}
                              onChange={(e) => setEditProcessControlData({ ...editProcessControlData, id: e.target.value })}
                            />
                          ) : (
                            item.id
                          )}</td>
                          <td>{editingProcessControlId === item.id ? (
                            <input
                              type="text"
                              value={editProcessControlData.application}
                              onChange={(e) => setEditProcessControlData({ ...editProcessControlData, application: e.target.value })}
                            />
                          ) : (
                            item.application
                          )}</td>
                          <td>{editingProcessControlId === item.id ? (
                            <input
                              type="text"
                              value={editProcessControlData.activity}
                              onChange={(e) => setEditProcessControlData({ ...editProcessControlData, activity: e.target.value })}
                            />
                          ) : (
                            item.activity
                          )}</td>
                          <td>{editingProcessControlId === item.id ? (
                            <input
                              type="text"
                              value={editProcessControlData.state}
                              onChange={(e) => setEditProcessControlData({ ...editProcessControlData, state: e.target.value })}
                            />
                          ) : (
                            item.state
                          )}</td>
                          <td className="action-cell">
                            {editingProcessControlId === item.id ? (
                              <div className="action-buttons">
                                <button className="action-btn save-btn" onClick={() => handleSaveProcessControl(item.id)}>
                                  <FaSave />
                                </button>
                                <button className="action-btn cancel-btn" onClick={() => setEditingProcessControlId(null)}>
                                  <FaTimes />
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button className="action-btn edit-btn" onClick={() => handleEditProcessControl(item)}>
                                  <FaEdit />
                                </button>
                                <button className="action-btn delete-btn" onClick={() => handleDeleteProcessControl(item.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                      {addingProcessControl && (
                        <tr>
                          <td>
                            <input
                              type="text"
                              value={newProcessControlData.id}
                              onChange={(e) => setNewProcessControlData({ ...newProcessControlData, id: e.target.value })}
                              placeholder="ID (e.g., buyer1, super2)"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newProcessControlData.application}
                              onChange={(e) => setNewProcessControlData({ ...newProcessControlData, application: e.target.value })}
                              placeholder="Application"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newProcessControlData.activity}
                              onChange={(e) => setNewProcessControlData({ ...newProcessControlData, activity: e.target.value })}
                              placeholder="Activity"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={newProcessControlData.state}
                              onChange={(e) => setNewProcessControlData({ ...newProcessControlData, state: e.target.value })}
                              placeholder="State"
                            />
                          </td>
                          <td className="action-cell">
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleAddProcessControl}>
                                <FaSave />
                              </button>
                              <button className="action-btn cancel-btn" onClick={() => setAddingProcessControl(false)}>
                                <FaTimes />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {isProcessControlExpanded && (
                    <button className="add-btn" onClick={() => setAddingProcessControl(true)}>
                      <FaPlus /> Add New
                    </button>
                  )}
                </div>
                {filteredProcessControls.length === 0 && !addingProcessControl && isProcessControlExpanded && (
                  <p className="no-match-message">No process controls match the current search.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Meta;